import { z } from 'zod';
import { viewFilterDTOSchema } from './filter-dto';
import { visualizationTypeSchema } from './visualization-dto';

export const exportFormatSchema = z.enum(['png', 'svg', 'pdf']);
export type ExportFormat = z.infer<typeof exportFormatSchema>;

export const pngOptionsSchema = z.object({
  transparentBackground: z.boolean().default(false),

  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  showLegend: z.boolean().default(true),

  visionStatement: z
    .string()
    .max(1000)
    .optional()
    .transform((val) => val?.trim() || undefined),

  viewportWidth: z.number().int().positive().optional(),
});
export type PngOptions = z.infer<typeof pngOptionsSchema>;

export const fontHandlingSchema = z.enum(['none', 'embed', 'outline']);

export const svgOptionsSchema = z.object({
  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  showLegend: z.boolean().default(true),

  visionStatement: z
    .string()
    .max(1000)
    .optional()
    .transform((val) => val?.trim() || undefined),

  fontHandling: fontHandlingSchema.optional(),
});
export type SvgOptions = z.infer<typeof svgOptionsSchema>;
export type SvgFontHandling = z.infer<typeof fontHandlingSchema>;

export const pdfOptionsSchema = z.object({
  useStandardPageSize: z.boolean().default(false),
  pageSize: z.enum(['A4', 'Letter', 'Legal', 'Slide16x9']).default('Letter'),
  orientation: z.enum(['portrait', 'landscape']).default('landscape'),

  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  showLegend: z.boolean().default(true),

  visionStatement: z
    .string()
    .max(1000)
    .optional()
    .transform((val) => val?.trim() || undefined),

  timelineWidth: z.number().int().min(595).max(4200).optional(),
  timelineHeight: z.union([z.number().int().min(595).max(4200), z.literal('auto')]).optional(),
});
export type PdfOptions = z.infer<typeof pdfOptionsSchema>;

export const dashboardSvgOptionsSchema = z.object({
  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  fontHandling: fontHandlingSchema.optional(),
});
export type DashboardSvgOptions = z.infer<typeof dashboardSvgOptionsSchema>;

export const dashboardPngOptionsSchema = z.object({
  showTitle: z.boolean().default(true),
  showTempoBranding: z.boolean().default(true),
  showPublishDate: z.boolean().default(true),
  transparentBackground: z.boolean().default(false),
  viewportWidth: z.number().int().positive().optional(),
});
export type DashboardPngOptions = z.infer<typeof dashboardPngOptionsSchema>;

export const exportOptionsSchema = z.object({
  png: pngOptionsSchema.optional(),
  svg: svgOptionsSchema.optional(),
  pdf: pdfOptionsSchema.optional(),
  dashboardSvg: dashboardSvgOptionsSchema.optional(),
  dashboardPng: dashboardPngOptionsSchema.optional(),
});
export type ExportOptions = z.infer<typeof exportOptionsSchema>;

export const exportTaskStateSchema = z.enum([
  'pending',
  'processing',
  'succeeded',
  'failed',
  'too_large',
  'expired',
]);
export type ExportTaskState = z.infer<typeof exportTaskStateSchema>;

export const exportTaskDispositionSchema = z.enum(['downloaded', 'error_seen']);
export type ExportTaskDisposition = z.infer<typeof exportTaskDispositionSchema>;

export const exportTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('visualization'),
    viewId: z.number().int(),
    visualizationId: z.number().int(),
    loopId: z.number().int(),
    visualizationType: visualizationTypeSchema,
  }),
  z.object({
    kind: z.literal('dashboard'),
    loopId: z.number().int(),
    dashboardId: z.number().int(),
  }),
]);
export type ExportTarget = z.infer<typeof exportTargetSchema>;
export type VisualizationExportTarget = Extract<ExportTarget, { kind: 'visualization' }>;
export type DashboardExportTarget = Extract<ExportTarget, { kind: 'dashboard' }>;

export const exportMessageSchema = z
  .object({
    taskId: z.number().int(),
    viewId: z.number().int().optional(),
    visualizationId: z.number().int().optional(),
    loopId: z.number().int(),
    organizationId: z.string().min(1),
    exportToken: z.string(),
    format: exportFormatSchema,
    options: exportOptionsSchema.optional(),
    retryCount: z.number().int().default(0),
    visualizationType: visualizationTypeSchema.default('swimlane'),
    // Optional during the rollout: requiring it fails in-flight messages from the previous
    // version at parse time.
    target: exportTargetSchema.optional(),
  })
  .refine(
    (m) => m.target !== undefined || (m.viewId !== undefined && m.visualizationId !== undefined),
    {
      message:
        'Export message must carry either a target or both viewId and visualizationId; a partial id pair is unroutable',
      path: ['target'],
    },
  );
export type ExportMessage = z.infer<typeof exportMessageSchema>;
export type ExportMessageInput = z.input<typeof exportMessageSchema>;

export function resolveExportTarget(message: ExportMessage): ExportTarget {
  if (message.target) return message.target;

  if (message.viewId === undefined || message.visualizationId === undefined) {
    throw new Error(
      `Export message for task ${message.taskId} has neither a target nor view/visualization ids`,
    );
  }

  return {
    kind: 'visualization',
    viewId: message.viewId,
    visualizationId: message.visualizationId,
    loopId: message.loopId,
    visualizationType: message.visualizationType,
  };
}

export const createExportRequestSchema = z.object({
  format: exportFormatSchema,
  options: exportOptionsSchema.optional(),
  filters: viewFilterDTOSchema.array().optional(),
});
export type CreateExportRequest = z.infer<typeof createExportRequestSchema>;

export const exportTaskDTOSchema = z.object({
  taskId: z.number().int(),
  viewId: z.number().int(),
  visualizationId: z.number().int(),
  organizationId: z.string(),
  format: exportFormatSchema,
  state: exportTaskStateSchema,
  options: exportOptionsSchema.optional(),
  s3Key: z.string().nullable(),
  error: z.string().nullable(),
  disposition: exportTaskDispositionSchema.nullable(),
  dispositionAt: z.iso.datetime().nullable(),
  retryCount: z.number().int(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
});
export type ExportTaskDTO = z.infer<typeof exportTaskDTOSchema>;

export const dashboardExportTaskDTOSchema = z.object({
  taskId: z.number().int(),
  dashboardId: z.number().int(),
  organizationId: z.string(),
  format: exportFormatSchema,
  state: exportTaskStateSchema,
  options: exportOptionsSchema.optional(),
  s3Key: z.string().nullable(),
  error: z.string().nullable(),
  disposition: exportTaskDispositionSchema.nullable(),
  dispositionAt: z.iso.datetime().nullable(),
  retryCount: z.number().int(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime(),
});
export type DashboardExportTaskDTO = z.infer<typeof dashboardExportTaskDTOSchema>;

export const exportStatusDTOSchema = z.object({
  taskId: z.number().int(),
  format: exportFormatSchema,
  state: exportTaskStateSchema,
  error: z.string().nullable(),
  disposition: exportTaskDispositionSchema.nullable(),
  retryCount: z.number().int(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
});
export type ExportStatusDTO = z.infer<typeof exportStatusDTOSchema>;

export const exportAcknowledgeErrorRequestSchema = z.object({
  taskId: z.number().int(),
});
export type ExportAcknowledgeErrorRequest = z.infer<typeof exportAcknowledgeErrorRequestSchema>;
